import React, { createContext, FC, useState, useEffect, useContext } from "react";
import { Feature, FeatureCollection, Geometry } from "geojson";
import type { LngLat, MapMouseEvent } from "react-map-gl/mapbox";
import { MarketExpansionCounty } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionCounty"
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { cloneDeep } from "lodash";
import { bbox } from "@turf/bbox";
import { LngLatBounds, LngLatBoundsLike, EasingOptions } from "mapbox-gl";
import { convertToFeatureCollection } from "@app/overviews/market-expansion-dashboard/shared/marketExpansionMapHelper";
import {
  MAP_BOUNDS_COUNTY_PADDING,
  MAP_BOUNDS_PADDING,
  MAP_PAN_DURATION,
} from '@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants';

interface MarketExpansionMapDataContextProps {
  dataBoundingBox: LngLatBoundsLike | undefined;
  dataLoaded: boolean;
  easingOptions: () => EasingOptions;
  getBoundingBoxFeature: (f: Feature | undefined) => LngLatBounds | undefined;
  handleMouseClickOnCounty: (e: MapMouseEvent) => void;
  handleMouseMoveOnCounty: (e: MapMouseEvent) => void;
  handleMouseLeaveOnCounty: () => void;
  hoverCountyBorderData: FeatureCollection<Geometry, MarketExpansionCounty> | undefined;
  hoveredCountySSA: string | null,
  minZoomLevel: number | null;
  mouseCoordinates: LngLat | null,
  mousedOverCountySSAs: string[] | null;
  resetMapData: () => void;
  selectedCountyBorderData: FeatureCollection<Geometry, MarketExpansionCounty> | undefined;
  selectedCountySSA: string | null,
  selectedTooltipHeight: number | null;
  setDataBoundingBox: (value: LngLatBoundsLike | undefined) => void;
  setDataLoaded: (value: boolean) => void;
  setMinZoomLevel: (value: number) => void;
  setMousedOverCountySSAs: (value: string[]) => void;
  setSelectedCountySSA: (value: string | null) => void;
  setSelectedTooltipHeight: (value: number | null) => void;
}

const initialValues: MarketExpansionMapDataContextProps = {
  dataBoundingBox: undefined,
  dataLoaded: false,
  easingOptions: () => undefined,
  getBoundingBoxFeature: () => undefined,
  handleMouseClickOnCounty: () => null,
  handleMouseLeaveOnCounty: () => null,
  handleMouseMoveOnCounty: () => null,
  hoverCountyBorderData: undefined,
  hoveredCountySSA: null,
  minZoomLevel: null,
  mouseCoordinates: null,
  mousedOverCountySSAs: null,
  resetMapData: () => null,
  selectedCountyBorderData: undefined,
  selectedCountySSA: null,
  selectedTooltipHeight: null,
  setDataBoundingBox: () => undefined,
  setDataLoaded: () => null,
  setMinZoomLevel: () => null,
  setMousedOverCountySSAs: () => null,
  setSelectedCountySSA: () => null,
  setSelectedTooltipHeight: () => null,
}

const MarketExpansionMapDataContext = createContext(initialValues);

const MarketExpansionMapDataContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {

  const filtersContext = useContext(MarketExpansionFiltersContext);

  const [hoverCountyBorderData, setHoverCountyBorderData] = useState<FeatureCollection<Geometry, MarketExpansionCounty> | undefined>(undefined);
  const [selectedCountyBorderData, setSelectedCountyBorderData] = useState<FeatureCollection<Geometry, MarketExpansionCounty> | undefined>(undefined);
  const [hoveredCountySSA, setHoveredCountySSA] = useState<string | null>(null);
  const [mousedOverCountySSAs, setMousedOverCountySSAs] = useState<string[]>([]);
  const [selectedCountySSA, setSelectedCountySSA] = useState<string | null>(null);
  const [selectedTooltipHeight, setSelectedTooltipHeight] = useState<number | null>(null);

  const [dataBoundingBox, setDataBoundingBox] = useState<LngLatBoundsLike | undefined>(undefined);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [minZoomLevel, setMinZoomLevel] = useState<number>(10);
  const [mouseCoordinates, setMouseCoordinates] = useState<LngLat | null>(null);

  const handleMouseMoveOnCounty = (e: MapMouseEvent) => {
    setMouseCoordinates(e.lngLat);
    setHoveredCountySSA(e.features?.[0].properties?.countySSA);
  }

  const handleMouseLeaveOnCounty = () => {
    setHoveredCountySSA(null);
  }

  useEffect(() => {
    setSelectedCountySSA(null);

    if (filtersContext.selectedState) {
      setSelectedCountyBorderData(convertToFeatureCollection([]));
      setHoverCountyBorderData(convertToFeatureCollection([]));
    }
  }, [filtersContext.countyOptions]);

  useEffect(() => {
    buildCountyBorderData();
  }, [filtersContext.selectedCounties, mousedOverCountySSAs])

  const resetMapData = (): void => {
    setSelectedCountySSA(null);
    setDataBoundingBox(undefined);
    setDataLoaded(false);
    setSelectedTooltipHeight(null);
  };

  useEffect(() => {
    if (selectedCountySSA) {
      const county = filtersContext.countyOptions.find(x => x.countySSA === selectedCountySSA);
      const feature = convertToFeatureCollection(county ? [county] : []).features[0];
      setDataBoundingBox(getBoundingBoxFeature(feature));
    } else if (filtersContext.countyOptions.length) {
      const [
        minLongitude,
        minLatitude,
        maxLongitude,
        maxLatitude
      ] = bbox(convertToFeatureCollection(filtersContext.countyOptions));

      setDataBoundingBox(new LngLatBounds([minLongitude, minLatitude], [maxLongitude, maxLatitude]));
    }
  }, [selectedCountySSA])

  const handleMouseClickOnCounty = (e: MapMouseEvent) => {
    const feature = e?.features?.[0];

    if (!feature?.properties?.countySSA) {
      return;
    }

    setSelectedCountySSA(feature?.properties?.countySSA);
  }

  const getBoundingBoxFeature = (f: Feature | undefined): LngLatBounds | undefined => {
    if (!f) {
      return undefined;
    }
    else {
      const [
        minLongitude,
        minLatitude,
        maxLongitude,
        maxLatitude
      ] = bbox(f);
      return new LngLatBounds([minLongitude, minLatitude], [maxLongitude, maxLatitude]);
    }
  }

  const buildCountyBorderData = () => {
    setSelectedCountyBorderData(undefined);

    let counties = cloneDeep(filtersContext.countyOptions);

    // hovered-over counties
    if (!!mousedOverCountySSAs?.length) {
      const hoveredCounties: MarketExpansionCounty[] = filtersContext.countyOptions.filter(
        x => mousedOverCountySSAs.includes(x.countySSA)
      );

      counties = counties.filter(
        x => !(mousedOverCountySSAs.includes(x.countySSA)))

      if (!!hoveredCounties) {
        const hoveredCountyBorderMap: FeatureCollection<Geometry, MarketExpansionCounty> = convertToFeatureCollection(hoveredCounties);
        setHoverCountyBorderData(hoveredCountyBorderMap);
      }
    } else {
      const hoveredCountyBorderMap: FeatureCollection<Geometry, MarketExpansionCounty> = convertToFeatureCollection([]);
      setHoverCountyBorderData(hoveredCountyBorderMap);
    }

    // selected counties
    const selectedCounties = convertToFeatureCollection(
      counties.filter((county) => filtersContext.selectedCounties.includes(county.countySSA))
    )

    // unselected counties
    const unselectedCounties = convertToFeatureCollection(
      counties.filter((county) => !filtersContext.selectedCounties.includes(county.countySSA))
    )

    if (selectedCounties.features.length || unselectedCounties.features.length) {
      setSelectedCountyBorderData(selectedCounties);
      setDataLoaded(true);
    }
  }

  const easingOptions = (): EasingOptions => {
    if (selectedCountySSA) {
      return {
        duration: MAP_PAN_DURATION,
        padding: {
          bottom: MAP_BOUNDS_COUNTY_PADDING,
          left: MAP_BOUNDS_COUNTY_PADDING,
          right: MAP_BOUNDS_COUNTY_PADDING,
          top: MAP_BOUNDS_COUNTY_PADDING + (selectedTooltipHeight ?? 0),
        },
        retainPadding: false,
      };
    }

    return {
      duration: MAP_PAN_DURATION,
      padding: MAP_BOUNDS_PADDING,
      retainPadding: false
    };
  }

  const contextValue: MarketExpansionMapDataContextProps = {
    dataBoundingBox,
    dataLoaded,
    easingOptions,
    getBoundingBoxFeature,
    handleMouseClickOnCounty,
    handleMouseLeaveOnCounty,
    handleMouseMoveOnCounty,
    hoverCountyBorderData,
    hoveredCountySSA,
    minZoomLevel,
    mouseCoordinates,
    mousedOverCountySSAs,
    resetMapData,
    selectedCountyBorderData,
    selectedCountySSA,
    selectedTooltipHeight,
    setDataBoundingBox,
    setDataLoaded,
    setMinZoomLevel,
    setMousedOverCountySSAs,
    setSelectedCountySSA,
    setSelectedTooltipHeight,
  }

  return (
    <MarketExpansionMapDataContext.Provider value={contextValue}>
      {props.children}
    </MarketExpansionMapDataContext.Provider>
  )
}

export { MarketExpansionMapDataContext, MarketExpansionMapDataContextProvider, MarketExpansionMapDataContextProps };
